// AI-assisted: Claude 2026-08-08, human-reviewed: ausstehend
// Startseite (Thors Wunsch Runde 17): der erste Blick nach dem Öffnen.
// Sie beantwortet drei Fragen — Wann war ich zuletzt dran? Wie weit bin ich?
// Was machen die anderen? — und bietet genau einen nächsten Schritt an.
//
// Arbeitsteilung mit dem Fortschritt: hier steht der Blick auf JETZT (Hero =
// letztes Training), dort die Entwicklung (Hero = Pflanze, die auch feiert).
// Deshalb wächst die Pflanze hier nur „ruhig" mit — gefeiert wird drüben.
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { Exercise, WorkoutEntry } from "@/lib/types"
import { getAllEntries, getExercises, onSynced, sofort, nachId } from "@/lib/data"
import { volume } from "@/lib/sets"
import { dayOf, fmtDate } from "@/lib/dates"
import { uniqueDates } from "@/lib/stats"
import { aktuellesLevel, questStats } from "@/lib/quests"
import { loadFeed, type FeedPost, type Reaction } from "@/lib/feed"
import { vergleichSatz } from "@/lib/vergleiche"
import { loadSession } from "@/lib/session"
import Trainingspflanze from "@/components/stats/Trainingspflanze"
import Fuchs from "@/components/celebration/creatures/Fuchs"
import { IconStift } from "@/components/icons"
import "@/styles/start.css"

type Post = FeedPost & { reactions: Reaction[] }

export default function StartPage() {
  const navigate = useNavigate()
  const [entries, setEntries] = useState<WorkoutEntry[]>(() => sofort.entries() ?? [])
  const [exercises, setExercises] = useState<Map<string, Exercise>>(() => nachId(sofort.exercises()))
  const [bereit, setBereit] = useState(() => sofort.entries() !== null)
  const [posts, setPosts] = useState<Post[]>([])
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    const load = async () => {
      const [es, exs] = await Promise.all([getAllEntries(), getExercises()])
      setEntries(es)
      setExercises(nachId(exs))
      setBereit(true)
    }
    void load()
    void loadSession().then((s) => setLaeuft(s !== null))
    // Feed ist online-only und Beiwerk: fällt er aus, bleibt der Rest stehen
    loadFeed(3).then(setPosts).catch(() => {})
    return onSynced(() => { void load() })
  }, [])

  const tage = uniqueDates(entries)
  const einheiten = tage.length
  const level = aktuellesLevel(questStats(entries))

  const letzterTag = tage[tage.length - 1] ?? ""
  const letzte = entries.filter((e) => dayOf(e.date) === letzterTag)
  const letztesVolumen = Math.round(letzte.reduce((s, e) => s + volume(e.sets), 0))
  const namen = [...new Set(letzte.map((e) => exercises.get(e.exercise)?.name).filter(Boolean))]
  const letzterVergleich = vergleichSatz(letztesVolumen)

  if (!bereit) return null

  if (einheiten === 0) {
    return (
      <div className="empty">
        <div className="critter"><Fuchs /></div>
        <p><b>Noch kein Training. Such dir einen Plan.</b></p>
        <button className="btn-secondary" onClick={() => navigate("/plaene")}>Plan aussuchen</button>
      </div>
    )
  }

  return (
    <>
      {/* Die einzige Voll-Tanne-Fläche dieser Seite (Skill §4/§5) */}
      <section className="card card--hero start-hero">
        <span className="stat-lbl">Zuletzt trainiert</span>
        <b className="start-datum">{fmtDate(letzterTag)}</b>
        <p className="start-zeile">
          {letzte.length} {letzte.length === 1 ? "Übung" : "Übungen"} · {letztesVolumen.toLocaleString("de-DE")} kg bewegt
        </p>
        {namen.length > 0 && (
          <p className="start-namen">
            {namen.slice(0, 3).join(" · ")}{namen.length > 3 ? ` · +${namen.length - 3}` : ""}
          </p>
        )}
        {letzterVergleich && <p className="start-vergleich">{letzterVergleich}</p>}
        <button className="save pressable start-los" onClick={() => navigate("/training")}>
          {laeuft ? "Training fortsetzen" : "Training starten"}
        </button>
      </section>

      <button type="button" className="start-fortschritt" onClick={() => navigate("/fortschritt")}>
        <span className="start-pflanze" aria-hidden="true"><Trainingspflanze units={einheiten} ruhig /></span>
        <span className="start-f-zahl">{einheiten}<i>{einheiten === 1 ? "Einheit" : "Einheiten"}</i></span>
        <span className="start-f-level">Level {level.stufe} – {level.name}</span>
        <span className="start-f-pfeil" aria-hidden="true">›</span>
      </button>

      <button type="button" className="btn-secondary pressable start-nachtragen" onClick={() => navigate("/log")}>
        <IconStift size={18} /> Satz nachtragen
      </button>

      {posts.length > 0 && (
        <section>
          <h2>Aus dem Feed</h2>
          <div className="start-feed">
            {posts.slice(0, 3).map((p) => (
              <div className="start-fp" key={p.id}>
                <span className="start-fp-alias">{p.alias}</span>
                <span className="start-fp-datum">{fmtDate(dayOf(p.date))}</span>
                <span className="start-fp-meta">
                  {p.exercises_count} {p.exercises_count === 1 ? "Übung" : "Übungen"} · {p.volume_kg.toLocaleString("de-DE")} kg
                </span>
              </div>
            ))}
          </div>
          <button type="button" className="btn-secondary pressable start-mehr" onClick={() => navigate("/feed")}>
            Alles im Feed
          </button>
        </section>
      )}
    </>
  )
}
